import { InsightState } from '@core/insights';
import { css } from '@emotion/react';
import {
  slideInLeft,
  theme,
  variantProps,
  variants,
} from '@web/features/theme';
import {
  addDays,
  endOfWeek,
  format,
  isSameDay,
  startOfWeek,
  subDays,
} from 'date-fns';
import { motion } from 'framer-motion';
import * as React from 'react';
import { pointColors, PointColors } from './spiral-utils';

export function InsightsCalendar({ date, wellnessChecks, type }: InsightState) {
  const today = date ? new Date(date) : new Date();

  const [isHelpOpen, setIsHelpOpen] = React.useState(false);
  const toggleHelp = () => setIsHelpOpen((prev) => !prev);

  const renderHeader = () => {
    const dateFormat = 'MMMM yyyy';

    return (
      <div css={styles.header} className="header row flex-middle">
        <div className="col col-center">
          <p>{format(today, dateFormat)}</p>
        </div>

        <button type="button" css={styles.help} onClick={toggleHelp}>
          ?
        </button>
      </div>
    );
  };

  const renderDaysOfTheWeek = () => {
    const dateFormat = 'EEEEE';
    const days = [];

    const startDate = startOfWeek(today);

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="col col-center" key={i}>
          {format(addDays(startDate, i), dateFormat)}
        </div>,
      );
    }

    return (
      <div css={styles.dayOfTheWeek} className="days row">
        {days}
      </div>
    );
  };

  const renderCells = () => {
    // start of this week - 21 days
    const startDate = subDays(startOfWeek(today), 21);
    // end of this week
    const endDate = endOfWeek(today);

    const dateFormat = 'd';
    const rows = [];

    let days = [];
    let day = startDate;
    let formattedDate = '';

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, dateFormat);
        
        // eslint-disable-next-line no-loop-func
        const wellnessCheck = wellnessChecks?.find((check) =>
          isSameDay(new Date(check.created_at), day),
        );

        const wellnessValue = wellnessCheck ? wellnessCheck[type] : null;
        
        const color =
          typeof wellnessValue === 'number'
            ? pointColors[wellnessValue]
            : PointColors.TRANSPARENT;

        days.push(
          <div
            key={+day}
            css={styles.cell}
            className={`col cell ${isSameDay(day, today) ? 'today' : ''}`}
          >
            <span style={{ backgroundColor: color }}>{formattedDate}</span>
          </div>,
        );

        day = addDays(day, 1);
      }

      rows.push(
        <div key={+day} css={styles.row} className="row">
          {days}
        </div>,
      );

      days = [];
    }

    return (
      <div css={styles.body} className="body" {...variantProps}>
        {rows}
      </div>
    );
  };

  return isHelpOpen ? (
    <motion.div
      css={styles.calendar}
      className="calendar"
      variants={slideInLeft}
      {...variantProps}
    >
      {renderHeader()}
      <div
        css={{
          padding: variants.space['8'],
          h2: {
            marginBottom: variants.space['4'],
          },
          p: {
            lineHeight: theme.lineHeights.tall,
            marginBottom: variants.space['4'],
          },
        }}
      >
        <h2>How to read your insights</h2>

        <p>
          Each insight tab takes your daily ratings from the check-ins and
          separates theme according to each check-in type.
        </p>

        <p>
          For each day you check in, a node is placed on the outermost ring of
          the time vortex with a color matched to each day's check-in (green =
          overall higher, red = overall lower).
        </p>

        <p>
          If you don't check in, no node is placed in the vortex. If there are
          more than 30 nodes, the oldest node is swallowed by into the time
          vortex.
        </p>

        <p>
          That way, you can see the last month of check-ins and look at the
          daily and weekly patterns over time.
        </p>
        <p>
          After 28 days, the oldest seed is removed from the calendar or
          swallowed into the time vortex.
        </p>
      </div>
    </motion.div>
  ) : (
    <div css={styles.calendar} className="rr-block calendar">
      {renderHeader()}
      {renderDaysOfTheWeek()}
      {renderCells()}
    </div>
  );
}

const styles = {
  calendar: css`
    flex: 1 1 100%;
    border: 1px solid ${theme.colors.whiteAlpha[900]};
    border-radius: ${theme.radii.xl};
    background: ${theme.colors.blackAlpha[100]};
    margin: ${theme.space[4]} auto;
  `,

  help: css`
    cursor: pointer;
    z-index: ${theme.zIndices.overlay}+7;
    position: absolute;
    display: block;
    top: 10px;
    right: 10px;
    background: ${theme.colors.whiteAlpha[900]};
    border: none;
    height: ${theme.space[5]};
    width: ${theme.space[5]};
    border-radius: ${theme.radii.full};
  `,

  body: css``,

  header: css`
    flex: 1 1 100%;
    backdrop-filter: blur(20px) !important;
  `,

  row: css`
    flex: 1 1 100%;
  `,

  dayOfTheWeek: css`
    flex: 1 1 100%;
    backdrop-filter: blur(20px);
  `,

  cell: css`
    pointer-events: none;
    flex: 1 1 100%;
    font-size: ${theme.fontSizes.xs} !important;
    line-height: 0;
    backdrop-filter: blur(20px) !important;

    &.today {
      background-color: ${theme.colors.info};
    }

    span {
      width: 100%;
      padding: ${theme.space[2]} 0;
      text-align: center;
      border-radius: 100%;
    }
  `,
};
